/**
 * Federated module entry for the account MFE.
 * Exposed as ./mfe-account; can be built from main (bundled) or from account (standalone).
 */

import {
    DynamicConfigController,
    MfeInitFunctionConfig,
    MfeInitFunctionResponse,
    MfeWrapper,
    psMfe
} from '@ps-refarch-ux/mfe-utils';
import {
    Breadcrumbs,
    applyNeonStylesToShadowDom,
    getBreadcrumbs
} from '@ps-refarch-ux/neon';
import { ReactElement } from 'react';
import { Root, createRoot } from 'react-dom/client';
import { AccountMfeContent } from '../app/AccountMfeContent';

export default psMfe({
    psMfeInit: function accountMfeInit(
        config: MfeInitFunctionConfig
    ): Promise<MfeInitFunctionResponse> {
        getBreadcrumbs().then((breadcrumbs: Breadcrumbs): void => {
            breadcrumbs.turnOnBreadcrumbsFor('smc-account');
        });

        const configController = new DynamicConfigController(config);

        const shadowRoot: ShadowRoot = config.element.attachShadow({
            mode: 'closed'
        });
        let reactRoot: Root | undefined = createRoot(shadowRoot);

        reactRoot.render(
            <div neon-react-root="">
                <MfeWrapper
                    configController={configController}
                    mfeComponent={(): ReactElement => (
                        <AccountMfeContent
                            open={true}
                            onClose={() => {
                                /* standalone: close is a no-op */
                            }}
                        />
                    )}
                />
            </div>
        );

        const remote: unknown =
            window['smc-account'.replace(/-/g, '_') as keyof Window];
        if (remote != null && typeof remote === 'object' && 'styleTags' in remote && remote.styleTags != null) {
            const styleTags = remote.styleTags as Map<string, unknown>;
            for (const style of Array.from(styleTags.values())) {
                shadowRoot.adoptedStyleSheets.push(style as CSSStyleSheet);
            }
        }

        const unloadShadowDom = applyNeonStylesToShadowDom(shadowRoot);

        return Promise.resolve({
            unloadMfe: function (): void {
                unloadShadowDom();
                setTimeout(() => {
                    if (reactRoot != null) {
                        reactRoot.unmount();
                        reactRoot = undefined;
                    }
                });
                getBreadcrumbs().then((breadcrumbs: Breadcrumbs): void => {
                    breadcrumbs.turnOffBreadcrumbsFor(
                        'smc-account'
                    );
                });
            },
            updateMfeConfig: function (
                newConfig: MfeInitFunctionConfig
            ): void {
                configController.updateConfig(newConfig);
            }
        });
    }
});
